import React, { useContext, useEffect, useRef } from "react";
import { useState } from "react";
import { Animated, Dimensions, Image, Linking, Platform, Pressable, ScrollView, TouchableOpacity } from "react-native";
import { StyleSheet, View } from "react-native"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import uuid from 'react-native-uuid';

import Icon from 'react-native-vector-icons/Ionicons';
import { PlaceColors } from "../helpers/PlaceColors";
import { useTranslation } from "react-i18next";
import { FindifyBasicModal } from "./FindifyBasicModal";
import { FindifyDataContext, FindifyDataContextType } from "@/contexts/FindifyDataContext";
import { PlaceText } from "./PlaceText";
import { getTranslatedString } from "../helpers/FindifyTranslatedString";
import Svg, { Path } from 'react-native-svg';
import { getFileUrl, IS_QA_MODE, setQAMODE } from "../../../config/environment";
import { DefaultShadow } from "../helpers/DefaultShadows";
import { openSocial } from "../PlaceInfo";
import { FindifyMenuContext, FindifyMenuContextType } from "@/contexts/FindifyMenuContext";
import { FindifyToast } from "./FindifyToast";
import Config from "react-native-config";
import { getApiFromModule } from "../helpers/GetContentsByModule";
import { getFeaturedImageUrl } from "../helpers/DetailsHelpers";

export const navigateToBottomBar = async ({
    navigation,
    path,
    params,
    replace,
}: {
    navigation: any,
    path: String,
    params?: any,
    replace?: boolean
}
) => {
    if (navigation.canGoBack()) {
        await navigation.popToTop();
    }
    if (replace) {
        await navigation.replace(path, params ?? {});
    } else {
        await navigation.push(path, params ?? {});
    }
}

export let BottomNavigationPadding = 0;

const updateBottomNavigationPadding = (findifyDataContext: FindifyDataContextType) => {
    if (findifyDataContext.visitWorkspaceSelected && findifyDataContext?.workspace?.app_settings?.enable_sidemenu) {
        BottomNavigationPadding = Platform.OS === 'ios' ? 10 : 20;
    } else {
        BottomNavigationPadding = Platform.OS === 'ios' ? 52 : 100;
    }
}

const openMenuItem = (item: any, navigation: any, t: any, findifyDataContext: FindifyDataContextType, isLogged: boolean, findifyMenuContext?: FindifyMenuContextType) => {
    const openExternalUrl = (url: string) => {
        if (Platform.OS === "web" && typeof window !== "undefined") {
            window.open(url, "_blank", "noopener,noreferrer");
            return;
        }
        Linking.openURL(url);
    }
    const getPlantsParams = (url?: string) => {
        if (!url) return {};
        const path = String(url).trim().toLowerCase().replace(/^\/+|\/+$/g, '').split('?')[0];
        if (path === 'plants' && !url.includes('?')) return {};
        if (path.startsWith('plants-category-')) {
            return { category_slug: path.slice('plants-category-'.length) };
        }
        if (path.startsWith('plants-')) {
            return { route_slug: path.slice('plants-'.length) };
        }
        const query = url.includes('?') ? url.split('?').slice(1).join('?') : '';
        if (!query) return {};
        const params = new URLSearchParams(query);
        return {
            route_prefix: params.get('route_prefix') || params.get('space_slug') || params.get('museum_slug') || undefined,
        };
    }
    const internalUrl = String(item?.url ?? item?.item_link ?? item?.path ?? '').trim().toLowerCase()
        .replace(/^\/+/, '').replace(/\/+$/, '').split('?')[0];
    const isInternalRoute = (route: string) => internalUrl === route || internalUrl === `#${route}`;
    const quizSlug = internalUrl.match(/^#?quiz\/(.+)$/)?.[1];
    const labelText = typeof item?.label === 'string'
        ? item.label.toLowerCase()
        : Object.values(item?.label ?? {}).join(' ').toLowerCase();

    // Findify menu items can arrive as URL, link, or legacy records. Resolve
    // the RMC routes before the type-specific handling so `network` cannot
    // fall through to WebViewPage.
    if (isInternalRoute('network') || labelText.includes('a rede') || labelText.includes('the network')) {
        navigateToBottomBar({ navigation, path: 'RmcNetwork' });
        return true;
    }
    if (isInternalRoute('home') || isInternalRoute('start') || isInternalRoute('territory')) {
        findifyMenuContext?.setActiveMuseumSlug?.(null);
        findifyMenuContext?.setActiveMuseumTitle?.(null);
        navigateToBottomBar({ navigation, path: 'RmcTerritoryHome' });
        return true;
    }
    if (isInternalRoute('quiz') || quizSlug) {
        navigateToBottomBar({ navigation, path: 'RmcQuiz', params: quizSlug ? { slug: quizSlug } : undefined });
        return true;
    }
    if (isInternalRoute('badges') || isInternalRoute('passport')) {
        navigateToBottomBar({ navigation, path: 'PassportPage' });
        return true;
    }

    switch (item.type) {
        case 'exit_museum':
            item.onExitMuseum?.();
            navigateToBottomBar({ navigation, path: 'Place', replace: true });
            break;
        case 'change_current_workspace':
            findifyDataContext?.setVisitWorkspaceSelected(false)
            break;
        case 'profile':
            navigateToBottomBar({ navigation, path: 'Profile' });
            break;
        case 'url':
            if (item.url && item.url.toLowerCase().includes('tictactoe')) {
                navigateToBottomBar({ navigation, path: 'TicTacToePage' });
            } else if (item.url && (item.url.toLowerCase().includes('memorygame') || item.url.toLowerCase().includes('memory-game'))) {
                navigateToBottomBar({ navigation, path: 'MemoryGamePage' });
            } else if (item.url && item.url.toLowerCase().includes('#passport')) {
                navigateToBottomBar({ navigation, path: 'PassportPage' });
            } else if (item.url && isInternalRoute('network')) {
                navigateToBottomBar({ navigation, path: 'RmcNetwork' });
            } else if (item.url && isInternalRoute('quiz')) {
                navigateToBottomBar({ navigation, path: 'RmcQuiz' });
            } else if (item.url && isInternalRoute('badges')) {
                navigateToBottomBar({ navigation, path: 'PassportPage' });
            } else if (item.url && item.url.toLowerCase().includes('plants')) {
                navigateToBottomBar({ navigation, path: 'PlantsPage', params: getPlantsParams(item.url) });
            } else if (item.url && (item.url.toLowerCase().startsWith('https://') || item.url.toLowerCase().startsWith('http://'))) {
                openExternalUrl(item.url);
            } else {
                navigateToBottomBar({ navigation, path: 'WebViewPage', params: { url: item.url, title: item.label } });
            }
            break;
        case 'module':
            switch (item.module) {
                case 'info':
                    navigateToBottomBar({ navigation, path: 'PlaceInfo' });
                    break;
                case 'map':
                    let props: any = {};
                    if (item.map_category) {
                        props['map_category'] = item.map_category;
                    }
                    navigateToBottomBar({ navigation, path: 'PlaceMap', params: props });
                    break;
                case 'scan_qr':
                    navigateToBottomBar({ navigation, path: 'ScanQR' });
                    break;
                case 'store':
                    navigateToBottomBar({ navigation, path: 'Store' });
                    break;
                case 'start':
                    findifyMenuContext?.setActiveMuseumSlug?.(null);
                    findifyMenuContext?.setActiveMuseumTitle?.(null);
                    navigateToBottomBar({ navigation, path: 'Place' });
                    break;
                case 'schedule':
                    navigateToBottomBar({ navigation, path: 'SchedulePage' });
                    break;
                case 'network':
                    navigateToBottomBar({ navigation, path: 'RmcNetwork' });
                    break;
                case 'quiz':
                    navigateToBottomBar({ navigation, path: 'RmcQuiz' });
                    break;
                case 'badges':
                    navigateToBottomBar({ navigation, path: 'PassportPage' });
                    break;
                case 'tickets':
                    navigateToBottomBar({ navigation, path: 'TicketsListZonePage' });
                    break;
                case 'checkout':
                    if (!isLogged) {
                        FindifyToast({
                            id: uuid.v4(),
                            visibilityTime: 1500,
                            type: 'error',
                            message: t('checkout.error_user_need_to_login'),
                        })
                        return false;
                    } else {
                        navigateToBottomBar({ navigation, path: 'CheckoutPage' });
                    }
                    break;
            }
            break;
        case 'content':
            navigateToBottomBar({
                navigation, path: 'PlaceContentDetails', params: {
                    module: 'contents',
                    content_id: item.content_id,
                    page_slug: item.page_slug,
                }
            });
            break;
        case 'point_of_interest':
            navigateToBottomBar({
                navigation, path: 'PlaceContentDetails', params: {
                    module: 'points_of_interest',
                    content_id: item.point_of_interest_id,
                    page_slug: item.page_slug,
                }
            });
            break;
        case 'contents':
            navigateToBottomBar({
                navigation,
                path: 'PlaceContentList',
                params: { module: item.content_type, category: item.content_category }
            })
            break;
        default: break;
    }
    return true
}

const makeModalMenuItem = (setModalOpen: any, item: any, index: any, navigation: any, t: any, findifyDataContext: any, isLogged: boolean) => {
    return <Pressable key={'modal_menu_item_' + index} onPressIn={() => {
        let success = openMenuItem(item, navigation, t, findifyDataContext, isLogged);
        if (success) {
            setModalOpen(false);
        }
    }}>
        <View style={modalStyle.item}>
            <Icon name={item?.icon} size={30} color={PlaceColors(findifyDataContext).bottomBarIcon.get()} />
            <PlaceText style={{ color: PlaceColors(findifyDataContext).bottomBarIcon.get() }}>{getTranslatedString(item.label, findifyDataContext)}</PlaceText>
        </View>
    </Pressable>
}

const makeBottomTabIcon = (path: string, icon: string, navigation: any, currentPage: string, findifyDataContext: any, isInitialPage = false, replace = true) => {
    return <Icon
        onPress={() => navigateToBottomBar({ navigation, path, replace })}
        name={icon}
        size={30}
        color={
            isInitialPage ? PlaceColors(findifyDataContext).background.get() :
                currentPage.startsWith(path) ?
                    PlaceColors(findifyDataContext).bottomBarIconSelected.get() :
                    PlaceColors(findifyDataContext).bottomBarIcon.get()} />
}

const makeFindifyBottomTabIcon = ({
    icon,
    label,
    active,
    onPress,
    findifyDataContext,
    isMuseumMenu,
}: {
    icon: string,
    label: string,
    active: boolean,
    onPress: Function,
    findifyDataContext: FindifyDataContextType,
    isMuseumMenu?: boolean,
}) => {
    const colors = PlaceColors(findifyDataContext);
    const menuTextColor = isMuseumMenu ? colors.primary.get() : colors.bottomBarIcon.get();
    const menuSelectedTextColor = isMuseumMenu ? colors.background.get() : colors.bottomBarIconSelected.get();
    const iconColor = active ? menuSelectedTextColor : menuTextColor;
    const textColor = active ? menuSelectedTextColor : menuTextColor;

    return <Pressable
        onPress={() => onPress()}
        style={[
            styles.findifyBottomItem,
            active && [
                styles.findifyBottomItemActive,
                { backgroundColor: isMuseumMenu ? colors.secondary.get() : colors.secondary.withAlpha(55) },
            ],
        ]}
    >
        <Icon name={icon} size={22} color={iconColor} />
        <PlaceText style={[styles.findifyBottomLabel, { color: textColor }]}>
            {label}
        </PlaceText>
    </Pressable>
}

const getFooterLogoImageUrl = (content: any) => {
    const featuredImage = getFeaturedImageUrl(content);
    if (featuredImage) return featuredImage;

    const galleryImage = content?.galleries?.[0]?.media?.file_path
        || content?.galleries?.[0]?.file?.file_path
        || content?.galleries?.[0]?.file_path
        || content?.gallery?.[0]?.media?.file_path
        || content?.gallery?.[0]?.file_path;

    return galleryImage ? `${getFileUrl()}${galleryImage}` : undefined;
}

export const FindifyFooterLogo = ({
    findifyDataContext,
    variant = 'bottom',
}: {
    findifyDataContext: FindifyDataContextType,
    variant?: 'bottom' | 'sidemenu',
}) => {
    const [imageUrl, setImageUrl] = useState<string | undefined>();

    useEffect(() => {
        let isMounted = true;
        if (Number(findifyDataContext?.workspace?.workspace_id) !== 159 || !findifyDataContext?.workspace?.primary_domain?.domain_name) {
            setImageUrl(undefined);
            return;
        }

        const api = getApiFromModule('contents', findifyDataContext);
        api?.get('logo-footer', null)
            .then((content: any) => {
                if (!isMounted) return;
                setImageUrl(getFooterLogoImageUrl(content));
            })
            .catch(() => {
                if (isMounted) setImageUrl(undefined);
            });

        return () => {
            isMounted = false;
        }
    }, [findifyDataContext?.workspace?.workspace_id, findifyDataContext?.workspace?.primary_domain?.domain_name]);

    if (!imageUrl) return null;

    return <View style={[
        styles.findifyFooterLogoWrap,
        variant === 'sidemenu' ? styles.findifyFooterLogoWrapSidemenu : styles.findifyFooterLogoWrapBottom,
    ]}>
        <Image
            source={{ uri: imageUrl }}
            resizeMode="contain"
            style={[
                styles.findifyFooterLogo,
                variant === 'sidemenu' ? styles.findifyFooterLogoSidemenu : null,
            ]}
        />
    </View>
}

const isFindifyEnglish = (language?: string) => language?.startsWith('en');
const findifyText = (findifyDataContext: FindifyDataContextType, pt: string, en: string) => (
    isFindifyEnglish(findifyDataContext?.language) ? en : pt
);

const getFindifyMenuPath = (item: any) => {
    const internalUrl = String(item?.url ?? item?.item_link ?? item?.path ?? '').trim().toLowerCase().replace(/^\/+/, '').replace(/\/+$/, '').split('?')[0];
    const isInternalRoute = (route: string) => internalUrl === route || internalUrl === `#${route}`;
    const quizSlug = internalUrl.match(/^#?quiz\/(.+)$/)?.[1];
    if (isInternalRoute('home') || isInternalRoute('start') || isInternalRoute('territory')) return 'RmcTerritoryHome';
    if (item?.type === 'url' && isInternalRoute('passport')) return 'PassportPage';
    if (item?.type === 'url' && isInternalRoute('network')) return 'RmcNetwork';
    if (item?.type === 'url' && (isInternalRoute('quiz') || quizSlug)) return 'RmcQuiz';
    if (item?.type === 'url' && item?.url?.toLowerCase?.().includes('plants')) return 'PlantsPage';
    if (item?.type === 'profile') return 'Profile';
    if (item?.type === 'content') return 'PlaceContentDetails';
    if (item?.type === 'contents') return 'PlaceContentList';
    if (item?.type === 'module') {
        switch (item.module) {
            case 'info': return 'PlaceInfo';
            case 'map': return 'PlaceMap';
            case 'scan_qr': return 'ScanQR';
            case 'store': return 'Store';
            case 'start': return 'Place';
            case 'schedule': return 'SchedulePage';
            case 'network': return 'RmcNetwork';
            case 'quiz': return 'RmcQuiz';
            case 'badges': return 'PassportPage';
            case 'tickets': return 'TicketsListZonePage';
            case 'checkout': return 'CheckoutPage';
            default: return '';
        }
    }
    return '';
}

const isFindifyMenuItemActive = (item: any, currentPage: string, currentParams: any) => {
    const path = getFindifyMenuPath(item);
    if (!path) return false;
    if (path === 'PlantsPage') return currentPage === 'PlantsPage' || currentPage === 'PlaceMap';
    if (path === 'PlaceContentDetails') {
        return currentPage === 'PlaceContentDetails'
            && currentParams?.module === 'contents'
            && Number(currentParams?.content_id) === Number(item?.content_id);
    }
    if (path === 'PlaceContentList') {
        return currentPage === 'PlaceContentList'
            && (!item?.content_type || currentParams?.module === item.content_type)
            && (!item?.content_category || Number(currentParams?.category) === Number(item.content_category));
    }
    return currentPage === path;
}

export const FindifyBottomNavigation = ({
    navigation,
    findifyDataContext,
    currentPage,
    currentParams,
    availableMenuItems,
    isLogged,
    isMuseumMenu,
    findifyMenuContext,
}: {
    navigation: any,
    findifyDataContext: FindifyDataContextType,
    currentPage: string,
    currentParams: any,
    availableMenuItems: any[],
    isLogged: boolean,
    isMuseumMenu?: boolean,
    findifyMenuContext?: FindifyMenuContextType,
}) => {
    const insets = useSafeAreaInsets();
    const { t } = useTranslation();
    const defaultBottomItems = [
        { type: 'module', module: 'start', icon: 'home-outline', label: { 'pt-PT': 'Início', 'en-US': 'Home', 'en-GB': 'Home' } },
        { type: 'module', module: 'map', icon: 'map-outline', label: { 'pt-PT': 'Mapa', 'en-US': 'Map', 'en-GB': 'Map' } },
        { type: 'module', module: 'scan_qr', icon: 'qr-code-outline', label: { 'pt-PT': 'Ler QR', 'en-US': 'Scan QR', 'en-GB': 'Scan QR' } },
        { type: 'module', module: 'badges', icon: 'medal-outline', label: { 'pt-PT': 'Emblemas', 'en-US': 'Badges', 'en-GB': 'Badges' } },
        { type: 'module', module: 'schedule', icon: 'calendar-outline', label: { 'pt-PT': 'Eventos', 'en-US': 'Events', 'en-GB': 'Events' } },
    ];
    const menuItems = (availableMenuItems?.length ? availableMenuItems : defaultBottomItems).slice(0, 5);

    return <View style={[
            styles.findifyBottomContainer,
            {
                position: Platform.OS === 'web' ? 'fixed' as any : 'absolute',
                paddingBottom: Math.max(insets.bottom, 8),
                backgroundColor: isMuseumMenu ? PlaceColors(findifyDataContext).background.get() : PlaceColors(findifyDataContext).bottomBar.get(),
                borderTopColor: isMuseumMenu ? PlaceColors(findifyDataContext).secondary.withAlpha(100) : PlaceColors(findifyDataContext).secondary.withAlpha(90),
                borderTopLeftRadius: isMuseumMenu ? 24 : 0,
                borderTopRightRadius: isMuseumMenu ? 24 : 0,
                shadowOpacity: isMuseumMenu ? 0.14 : 0.08,
                shadowRadius: isMuseumMenu ? 14 : 10,
            },
        ]}>
            {menuItems.map((item: any, index: number) => <React.Fragment key={`findify-bottom-${index}`}>
	                {makeFindifyBottomTabIcon({
                    icon: item?.icon || defaultBottomItems[index]?.icon || 'ellipse-outline',
                    label: getTranslatedString(item?.label, findifyDataContext) || defaultBottomItems[index]?.label?.['pt-PT'] || '',
                    active: isFindifyMenuItemActive(item, currentPage, currentParams),
	                    onPress: () => openMenuItem(item, navigation, t, findifyDataContext, isLogged, findifyMenuContext),
	                findifyDataContext,
                    isMuseumMenu,
	            })}
        </React.Fragment>)}
    </View>
}

const groupMenuItems = (items: any[]) => {
    const divider = items.length > 9 ? 4 : 3;
    const rows: any[] = [];
    for (let i = 0; i < items.length; i += divider) {
        rows.push(items.slice(i, i + divider));
    }
    return rows;
}

const getMenuTitleValues = (menu: any) => {
    const values = [
        menu?.title,
        menu?.name,
        menu?.label,
        menu?.description,
        menu?.page_title,
    ];
    return values.flatMap((value: any) => {
        if (!value) return [];
        if (typeof value === 'string') return [value];
        if (typeof value === 'object') return Object.values(value).filter(Boolean).map((line: any) => String(line));
        return [];
    });
}

const getMenuKeyValues = (menu: any) => [
    ...getMenuTitleValues(menu),
    menu?.page_slug,
    menu?.slug,
    menu?.menu_slug,
    menu?.key,
    menu?.code,
    menu?.identifier,
].filter(Boolean).map((line: any) => String(line).trim().toLocaleLowerCase());

const getRouteMuseumSlug = (currentPage: string, currentParams: any) => {
    if (currentPage !== 'PlaceContentDetails') return '';
    if (currentParams?.module !== 'contents') return '';
    return String(currentParams?.page_slug ?? '').trim().toLocaleLowerCase();
}

const makeExitMuseumMenuItem = (findifyMenuContext: FindifyMenuContextType) => ({
    type: 'exit_museum',
    label: {
        'pt-PT': 'Sair do núcleo',
        'en-GB': 'Exit museum',
        'en-US': 'Exit museum',
    },
    icon: 'exit-outline',
    onExitMuseum: () => {
        findifyMenuContext.setActiveMuseumSlug(null);
        findifyMenuContext.setActiveMuseumTitle(null);
        findifyMenuContext.setIsSidemenuOpen(false);
    },
});

const normalizeMenuItem = (item: any) => {
    const source = {
        ...item,
        ...(item?.menu_item ?? {}),
        ...(item?.item ?? {}),
    };

    if (source.type || source.module || source.url || source.content_id) return source;

    const label = source.item_name ?? source.label ?? source.title ?? source.name;
    const itemType = String(source.item_type ?? '').trim().toLocaleLowerCase();
    const itemId = source.item_id;
    const itemLink = source.item_link;

    switch (itemType) {
        case 'contents':
        case 'content':
            return {
                ...source,
                type: 'content',
                label,
                content_id: itemId,
                page_slug: itemLink,
                icon: source.icon ?? 'document-text-outline',
            };
        case 'points_of_interest':
        case 'point_of_interest':
        case 'poi':
            return {
                ...source,
                type: 'point_of_interest',
                label,
                point_of_interest_id: itemId,
                page_slug: itemLink,
                icon: source.icon ?? 'location-outline',
            };
        case 'contents_categories':
        case 'content_categories':
            return {
                ...source,
                type: 'contents',
                label,
                content_type: 'contents',
                content_category: itemId,
                icon: source.icon ?? 'albums-outline',
            };
        case 'points_of_interest_categories':
        case 'point_of_interest_categories':
        case 'pois_categories':
            return {
                ...source,
                type: 'contents',
                label,
                content_type: 'points_of_interest',
                content_category: itemId,
                icon: source.icon ?? 'map-outline',
            };
        case 'events_categories':
        case 'event_categories':
            return {
                ...source,
                type: 'contents',
                label,
                content_type: 'events',
                content_category: itemId,
                icon: source.icon ?? 'calendar-outline',
            };
        case 'store':
            return {
                ...source,
                type: 'module',
                module: 'store',
                label,
                icon: source.icon ?? 'bag-outline',
            };
        case 'external':
        case 'url':
        case 'link':
            return {
                ...source,
                type: 'url',
                url: itemLink,
                label,
                icon: source.icon ?? 'open-outline',
            };
        default:
            return {
                ...source,
                type: itemLink ? 'url' : source.type,
                url: itemLink,
                label,
                icon: source.icon ?? 'ellipse-outline',
            };
    }
};

export const FindifyNavigation = ({ navigation }: any) => {
    const findifyMenuContext = useContext(FindifyMenuContext)

    const [availableModules, setAvailableModules] = useState<any>([]);
    const [sideMenuModules, setSideMenuModules] = useState<any>([]);
    const [sideMenuItems, setSideMenuItems] = useState<any[]>([]);
    const [availableMenuItems, setAvailableMenuItems] = useState<any[]>([]);
    const { t, i18n } = useTranslation();
    const findifyDataContext = useContext(FindifyDataContext);
    const navState = navigation.getState();
    const insets = useSafeAreaInsets();
    // const [mode, setMode] = useState<'bottomNav' | 'sideMenu'>('bottomNav');
    // const currentPage = navState.routeNames[navState.index];
    const currentRoute = navState.routes[navState.index];
    const currentPage = currentRoute.name;
    const currentParams = currentRoute.params ?? {};
    const routeMuseumSlug = getRouteMuseumSlug(currentPage, currentParams);
    const effectiveMuseumSlug = findifyMenuContext.activeMuseumSlug || routeMuseumSlug;

    const isInitialPage = !findifyDataContext.visitWorkspaceSelected;

    const [QAPressTimer, setQAPressTimer] = useState(null);
    const [QAPressCount, setQAPressCount] = useState(0);

    const [isLogged, setIsLogged] = useState(false);

    useEffect(() => {
        if (findifyDataContext?.userCustomerAccounts[findifyDataContext?.workspace?.workspace_id]) {
            setIsLogged(true);
        } else {
            setIsLogged(false);
        }
    }, [findifyDataContext?.userCustomerAccounts]);

    // QA MODE
    useEffect(() => {
        if (QAPressCount === 15) {
            // Execute your function when the condition is met
            setQAMODE(!IS_QA_MODE);
            if (navigation.canGoBack()) {
                navigation.popToTop();
            }
            navigation.replace('IntialPage', { IS_QA_MODE });
            // Reset the press count
            setQAPressCount(0);
        }
    }, [QAPressCount]);


    useEffect(() => {
        updateBottomNavigationPadding(findifyDataContext);
    }, [findifyDataContext.visitWorkspaceSelected, findifyDataContext?.workspace?.app_settings?.enable_sidemenu]);

    useEffect(() => {
        const modules = findifyDataContext?.workspace?.app_settings?.app_menu_lines ?? [];
        setAvailableMenuItems(modules);

        findifyMenuContext.setIsSidemenu(!isInitialPage && findifyDataContext?.workspace?.app_settings?.enable_sidemenu);

        setAvailableModules(groupMenuItems(modules));
    }, [i18n.language]);

    useEffect(() => {
        let isMounted = true;
        if (!findifyDataContext?.visitWorkspaceSelected || !findifyDataContext?.workspace?.primary_domain?.domain_name) {
            setSideMenuItems([]);
            setSideMenuModules([]);
            findifyMenuContext.setHasSidemenuItems(false);
            return;
        }

        const menusApi = getApiFromModule('menus', findifyDataContext);
        menusApi?.getList(0, 100, null, {}, null)
            .then((res: any) => {
                const menus = res?.results ?? [];
                const targetMenuKey = effectiveMuseumSlug
                    ? `menu-app-${effectiveMuseumSlug}`
                    : 'menu-app';
                const menu = menus.find((line: any) => getMenuKeyValues(line).includes(targetMenuKey))
                    ?? menus.find((line: any) => getMenuKeyValues(line).includes('menu-app'))
                    ?? menus.find((line: any) => getMenuKeyValues(line).includes('menu'));
                if (menu?.items?.length) return { results: menu.items };
                if (!menu?.menu_id) return [];
                const menuItemsApi = getApiFromModule('menus_items', findifyDataContext, menu.menu_id);
                return menuItemsApi?.getList(0, 100, null, {}, null);
            })
            .then((res: any) => {
                if (!isMounted) return;
                const items = (Array.isArray(res) ? res : res?.results ?? [])
                    .map(normalizeMenuItem)
                    .filter((item: any) => item && (item.type || item.module || item.url || item.content_id || item.point_of_interest_id));
                if (effectiveMuseumSlug) {
                    items.push(makeExitMuseumMenuItem(findifyMenuContext));
                }
                setSideMenuItems(items);
                setSideMenuModules(groupMenuItems(items));
                findifyMenuContext.setHasSidemenuItems(items.length > 0);
            })
            .catch(() => {
                if (!isMounted) return;
                setSideMenuItems([]);
                setSideMenuModules([]);
                findifyMenuContext.setHasSidemenuItems(false);
            });

        return () => {
            isMounted = false;
        }
    }, [findifyDataContext?.visitWorkspaceSelected, findifyDataContext?.workspace?.workspace_id, findifyDataContext?.workspace?.primary_domain?.domain_name, findifyMenuContext.activeMuseumSlug, routeMuseumSlug, i18n.language]);

    const hasFindifyOverflowMenu = !isInitialPage
        && Number(findifyDataContext?.workspace?.workspace_id) === 159
        && !findifyMenuContext.isSidemenu
        && (sideMenuItems.length > 0 || availableMenuItems.length > 4);

    const sidemenuModulesToRender = sideMenuModules.length > 0 ? sideMenuModules : availableModules;

    // sidemenu
    return <>
        {
            findifyMenuContext.isSidemenu &&
            <SideMenuNavigationElement isLogged={isLogged} navigation={navigation} findifyDataContext={findifyDataContext} currentPage={currentPage} availableModules={sidemenuModulesToRender} />
        }
        {
            hasFindifyOverflowMenu &&
            <SideMenuNavigationElement isLogged={isLogged} navigation={navigation} findifyDataContext={findifyDataContext} currentPage={currentPage} availableModules={sidemenuModulesToRender} />
        }
        {
            !findifyMenuContext.isSidemenu &&
            <BottomNavigationElement
                isLogged={isLogged}
                navigation={navigation}
                isInitialPage={isInitialPage}
                findifyDataContext={findifyDataContext}
                currentPage={currentPage}
                currentParams={currentParams}
                availableModules={availableModules}
                availableMenuItems={effectiveMuseumSlug && sideMenuItems.length > 0 ? sideMenuItems : availableMenuItems}
                isMuseumMenu={!!effectiveMuseumSlug && sideMenuItems.length > 0}
                findifyMenuContext={findifyMenuContext}
            />
        }
        {/* ENABLE QA MODE */}
        <Pressable
            style={{
                position: 'absolute',
                bottom: insets.bottom,
                right: 0,
                // height: 40,
                padding: 10,
                backgroundColor: 'red',
                zIndex: (isInitialPage || IS_QA_MODE) ? 100 : -10,
                opacity: IS_QA_MODE ? 1 : 0,
            }}
            onPress={() => {
                // ONLY ENABLE OR DISABLE ON INITIAL PAGE
                if (isInitialPage) {
                    setQAPressCount(QAPressCount + 1);
                    // Set a timer to reset the press count 
                    if (!QAPressTimer) {

                        const pressTimer = setTimeout(() => {
                            setQAPressCount(0);
                            setQAPressTimer(null);
                        }, 6000);
                        setQAPressTimer(pressTimer);
                    }

                    return () => {
                        // Clear the timer if the component unmounts
                        if (QAPressTimer) {
                            clearTimeout(QAPressTimer);
                        }
                    };
                }
            }}
        >
            <View>
                <PlaceText>{IS_QA_MODE ? 'QA' : 'PROD'}</PlaceText>
            </View>
        </Pressable>
    </>

}


const makeSidemenuLine = (item: any, index: any, navigation: any, t: any, findifyDataContext: FindifyDataContextType, findifyMenuContext: FindifyMenuContextType, isLogged: boolean) => {
    return <Pressable key={'sidemenu_item_' + index} onPressIn={() => {
        let success = openMenuItem(item, navigation, t, findifyDataContext, isLogged);
        if (success) {
            findifyMenuContext.setIsSidemenuOpen(false);
        }
    }}>
        <View style={{
            display: 'flex',
            flexDirection: 'row',
            paddingVertical: 8,
            paddingHorizontal: 16,
            alignContent: 'center',
            alignItems: 'center',
            gap: 12,
            marginVertical: 2,
            marginHorizontal: 8,
            borderRadius: 12,
        }}>
            <Icon name={item?.icon} size={26} color={PlaceColors(findifyDataContext).sidebarTextColor.get()} />
            <PlaceText style={{ color: PlaceColors(findifyDataContext).sidebarTextColor.get(), fontSize: 16, fontWeight: '500' }}>{getTranslatedString(item.label, findifyDataContext)}</PlaceText>
        </View>
    </Pressable>
}

export const toggleSidemenu = (findifyMenuContext: FindifyMenuContextType) => findifyMenuContext.setIsSidemenuOpen(!findifyMenuContext.isSidemenuOpen);
const SideMenuNavigationElement = ({
    navigation,
    findifyDataContext,
    currentPage,
    availableModules,
    isLogged,
}: {
    navigation: any,
    findifyDataContext: any,
    currentPage: string,
    availableModules: any,
    isLogged: boolean
}) => {

    const findifyMenuContext = useContext(FindifyMenuContext);
    const insets = useSafeAreaInsets();
    const opacity = useRef(new Animated.Value(0)).current;
    const translateX = useRef(new Animated.Value(0)).current;
    const { t } = useTranslation();
    const [renderImageSize, setRenderImageSize] = useState<any>({});

    useEffect(() => {
        if (findifyDataContext.workspace?.app_settings?.app_logo_url) {
            Image.getSize(findifyDataContext.workspace?.app_settings?.app_logo_url, (w, h) => {
                const aspectRatio = w / h;

                const maxHeight = 100;
                const maxWidth = Dimensions.get('window').width * 0.60 * 0.9; // Sidebar is 60% of screen, logo up to 90% of sidebar

                let width = maxWidth;
                let height = maxHeight;

                if (aspectRatio > 1) {
                    // Landscape image
                    height = maxWidth / aspectRatio;
                } else if (aspectRatio < 1) {
                    // Portrait image
                    width = maxHeight * aspectRatio;
                }

                // Ensure the calculated dimensions are within the specified bounds
                if (width > maxWidth) {
                    width = maxWidth;
                    height = width / aspectRatio;
                }

                if (height > maxHeight) {
                    height = maxHeight;
                    width = height * aspectRatio;
                }

                setRenderImageSize({
                    width,
                    height,
                })
            });
        }
    }, [findifyDataContext.workspace?.app_settings?.app_logo_url]);

    useEffect(() => {
        Animated.timing(translateX, {
            toValue: (!findifyMenuContext.isSidemenuOpen ? 0 : (Dimensions.get('window').width * 2)),
            duration: 400, // Animation duration in milliseconds
            useNativeDriver: true, // You can set this to true if using some transforms for better performance
        }).start();
        Animated.timing(opacity, {
            toValue: (!findifyMenuContext.isSidemenuOpen ? 0 : 1),
            delay: !findifyMenuContext.isSidemenuOpen ? 0 : 200,
            duration: 200, // Animation duration in milliseconds
            useNativeDriver: true, // Using the native driver for performance
        }).start();
    }, [findifyMenuContext.isSidemenuOpen]);



    return <>
        <Animated.View
            style={{
                left: '-200%',
                position: 'absolute',
                top: 0,
                zIndex: 10,
                width: '200%',
                height: '100%',
                opacity,
                transform: [{ translateX }],
                backgroundColor: '#00000080',
            }}
        >

            <View
                style={{
                    backgroundColor: PlaceColors(findifyDataContext).sidebarBackgroundColor.get(),
                    zIndex: 10,
                    width: '30%', // 60 / 4 as container is 4x screen width
                    height: '100%',
                    paddingTop: insets.top,
                    paddingBottom: insets.bottom,
                    borderTopRightRadius: 32,
                    borderBottomRightRadius: 32,
                    ...DefaultShadow,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                }}
            >
                <ScrollView
                    showsVerticalScrollIndicator={false}
                    showsHorizontalScrollIndicator={false}
                    bounces={false}
                    contentContainerStyle={{
                        display: 'flex',
                        flexDirection: 'column',
                        marginLeft: 10,
                    }}
                >
                    <Pressable
                        onPress={() => toggleSidemenu(findifyMenuContext)}
                    >
                        {makeSidemenuLine({
                            label: '',
                            icon: 'close-outline',
                        }, -1, navigation, t, findifyDataContext, findifyMenuContext, isLogged)}
                    </Pressable>
                    {
                        findifyDataContext.workspace?.app_settings?.app_logo_url &&
                        <View style={{
                            paddingBottom: 10,
                            marginBottom: 10,
                            marginTop: -10, // Pull up closer to close button
                            borderBottomWidth: 1,
                            borderBottomColor: PlaceColors(findifyDataContext).sidebarTextColor.withAlpha(50),
                            alignItems: 'center',
                            width: '100%',
                            alignSelf: 'center'
                        }}>
                            <Image
                                style={{
                                    ...renderImageSize,
                                }}
                                resizeMode="contain"
                                source={{ uri: findifyDataContext.workspace?.app_settings?.app_logo_url }} />
                        </View>
                    }
                    {availableModules.map((row: any, rindex: number) => {
                        return <>
                            {row.map((item: any, index: number) => {
                                return makeSidemenuLine(item, `sidemenu-line-${rindex}-${index}`, navigation, t, findifyDataContext, findifyMenuContext, isLogged);
                            })}
                        </>
                    })}

                </ScrollView>
                <View
                    style={{
                        paddingHorizontal: 20,
                        paddingBottom: 10,
                        paddingTop: 5,
                        display: 'flex',
                        gap: 4
                    }}
                >
                    <FindifyFooterLogo findifyDataContext={findifyDataContext} variant="sidemenu" />
                    {findifyDataContext.workspace?.app_settings?.socials_lines.map((line: any, index: number) => {
                        return makeSocialLine(line?.icon, index, getTranslatedString(line?.label, findifyDataContext), findifyDataContext, () => openSocial(navigation, line, t));
                    })}
                    <View style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', gap: 4, borderTopColor: PlaceColors(findifyDataContext).sidebarTextColor.get(), borderTopWidth: 1, paddingTop: 5, marginTop: 2 }}>
                        {makeSocialLine('person-circle-outline', 45132, t('profile_tab.profile'), findifyDataContext, () => {
                            openMenuItem({
                                icon: 'person-circle-outline',
                                label: t('profile_tab.profile'),
                                type: 'profile',
                            }, navigation, t, findifyDataContext, isLogged);
                            findifyMenuContext.setIsSidemenuOpen(false);
                        })}
                        {!Config.WORKSPACE_ID &&  makeSocialLine('chevron-back-outline', 54323, t('profile_tab.change_place'), findifyDataContext, () => {
                            openMenuItem({
                                icon: 'chevron-back-outline',
                                label: t('profile_tab.change_place'),
                                type: 'change_current_workspace',
                            }, navigation, t, findifyDataContext, isLogged);
                            findifyMenuContext.setIsSidemenuOpen(false);
                        })}
                    </View>
                </View>
            </View>
        </Animated.View>
    </>

}

const makeSocialLine = (icon: any, index: number, label: string, findifyDataContext: FindifyDataContextType, action: Function) => {
    return <Pressable
        key={'social_line_key' + index}
        style={{
            display: 'flex',
            flexDirection: 'row',
            gap: 6,
            paddingVertical: 2
        }}
        onPress={() => action()}>
        <Icon style={{ color: PlaceColors(findifyDataContext).sidebarTextColor.get() }} name={icon} size={16} />
        <PlaceText
            style={{
                color: PlaceColors(findifyDataContext).sidebarTextColor.get(),
            }}
        >{label}</PlaceText>
    </Pressable>
}

const BottomNavigationElement = ({
    navigation,
    isInitialPage,
    findifyDataContext,
    currentPage,
    currentParams,
    availableModules,
    availableMenuItems,
    isLogged,
    isMuseumMenu,
    findifyMenuContext,
}: {
    navigation: any,
    isInitialPage: boolean,
    findifyDataContext: any,
    currentPage: string,
    currentParams: any,
    availableModules: any,
    availableMenuItems: any[],
    isLogged: boolean,
    isMuseumMenu?: boolean,
    findifyMenuContext?: FindifyMenuContextType,
}) => {
    const insets = useSafeAreaInsets();
    const [modalOpen, setModalOpen] = useState(false);
    const { t } = useTranslation();

    if (!isInitialPage && Number(findifyDataContext?.workspace?.workspace_id) === 159) {
        return <FindifyBottomNavigation
            navigation={navigation}
            findifyDataContext={findifyDataContext}
            currentPage={currentPage}
            currentParams={currentParams}
            availableMenuItems={availableMenuItems}
            isLogged={isLogged}
            isMuseumMenu={isMuseumMenu}
            findifyMenuContext={findifyMenuContext}
        />
    }

    return <>
        {!isInitialPage && <FindifyBasicModal
            // isOpen={true}
            transparent={true}
            isOpen={modalOpen}
            closeModal={() => setModalOpen(false)}
            align="flex-end"
        >
            <View style={[modalStyle.modalView, { marginBottom: insets.bottom + 85, backgroundColor: PlaceColors(findifyDataContext).primary.withAlpha(220) }]}>
                {availableModules.map((row: any, rindex: number) => {
                    return <View key={'bottom_nav_row_' + rindex} style={modalStyle.modalRow}>
                        {row.map((item: any, index: number) => {
                            return makeModalMenuItem(setModalOpen, item, `_${rindex}_${index}`, navigation, t, findifyDataContext, isLogged);
                        })}
                    </View>
                })}
            </View>
        </FindifyBasicModal>}

        <View style={[styles.conatiner, {
            marginBottom: insets.bottom,
            borderTopColor: PlaceColors(findifyDataContext).primary.get(),
            backgroundColor: PlaceColors(findifyDataContext).bottomBar.get(),
            height: 50,
        }]} >
            {isInitialPage ? <>
                {makeBottomTabIcon('ScanQR', 'qr-code-outline', navigation, currentPage, findifyDataContext, isInitialPage, false)}
            </> : <>
                {makeBottomTabIcon('Place', 'information-circle-outline', navigation, currentPage, findifyDataContext)}
                {<TouchableOpacity
                    style={[styles.button, {
                        backgroundColor: PlaceColors(findifyDataContext).findifyPrimary.get(),
                    }]}
                    onPress={() => { setModalOpen(true); }}
                >
                    <Svg width={50} height={50} color={'#FFFFFF'}>
                        <Path
                            fill="#FFFFFF"
                            d="M34.957 7.69c2.885 2.34 4.768 5.405 5.729 8.977.811 8.845-2.087 14.991-7.506 21.875-4.517 5.358-4.517 5.358-7.2 7.046-2.673-.037-4.147-1.762-5.999-3.5-3.76-3.894-7.139-8.122-9.197-13.166l-.469-1.005c-1.714-4.157-1.749-9.726-.235-13.946 2.315-4.776 5.65-7.089 10.508-9.069 5.036-1.552 10.115-.121 14.369 2.788Zm-14.369 8.486c-1.198 1.43-1.449 2.28-1.685 4.136.26 2.11.79 3.083 2.175 4.687 1.698 1.17 2.62 1.476 4.688 1.44 2.183-.596 3.453-1.478 4.779-3.309.717-2.281.607-3.963-.46-6.097-1.285-1.487-2.271-2.13-4.105-2.817-2.264 0-3.614.647-5.392 1.961Z"
                        />
                    </Svg>
                </TouchableOpacity>}
                {makeBottomTabIcon('Profile', 'person-circle-outline', navigation, currentPage, findifyDataContext)}
            </>
            }
        </View >
    </>
}
const modalStyle = StyleSheet.create({
    modalView: {

        width: '85%',
        display: 'flex',
        borderRadius: 32,
        paddingVertical: 20,
        // padding: 25,
        alignItems: 'center',
        shadowColor: '#000',
        shadowOffset: {
            width: 0,
            height: 10,
        },
        shadowOpacity: 0.3,
        shadowRadius: 15,
        elevation: 10,

    },
    modalRow: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-evenly',
        alignItems: 'center',
        width: '100%',
        marginVertical: 10,
        gap: 10,
    },
    item: {
        display: 'flex',
        width: 90,

        // height: '100%',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 12,
        borderRadius: 16,
        color: '#fff',
        textDecorationColor: '#fff',
    }
})

const styles = StyleSheet.create({
    //Modal
    //Base
    button: {
        top: -10,
        justifyContent: 'center',
        alignItems: 'center',
        width: 70,
        height: 70,
        margin: -15,
        borderRadius: 40,
        fontSize: 20,
    },
    buttonIcon: {
        color: '#F6F7EB'
    },
    conatiner: {
        paddingTop: 10,
        flexDirection: 'row',
        width: '100%',
        zIndex: 3,
        borderTopWidth: 3,
        justifyContent: 'space-around',
        position: 'absolute',
        bottom: 0,
    },
    findifyBottomContainer: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 3,
        minHeight: 76,
        paddingTop: 7,
        paddingHorizontal: 8,
        borderTopWidth: 1,
        flexDirection: 'row',
        alignItems: 'stretch',
        justifyContent: 'space-between',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: -2 },
        shadowOpacity: 0.08,
        shadowRadius: 10,
        elevation: 6,
    },
    findifyFooterLogoWrap: {
        width: '100%',
        alignItems: 'center',
        justifyContent: 'center',
    },
    findifyFooterLogoWrapBottom: {
        minHeight: 50,
        marginTop: 28,
        paddingHorizontal: 22,
        paddingTop: 14,
        paddingBottom: 10,
        backgroundColor: 'rgba(250, 247, 241, 0.94)',
        borderTopWidth: 1,
        borderTopColor: 'rgba(153, 0, 0, 0.10)',
    },
    findifyFooterLogoWrapSidemenu: {
        paddingBottom: 12,
        marginBottom: 8,
        borderBottomWidth: 1,
        borderBottomColor: 'rgba(255,255,255,0.25)',
    },
    findifyFooterLogo: {
        width: '70%',
        maxWidth: 220,
        height: 34,
    },
    findifyFooterLogoSidemenu: {
        width: '88%',
        maxWidth: 180,
        height: 42,
    },
    findifyBottomItem: {
        flex: 1,
        minHeight: 58,
        marginHorizontal: 2,
        borderRadius: 16,
        alignItems: 'center',
        justifyContent: 'center',
        gap: 3,
        paddingHorizontal: 2,
        paddingVertical: 6,
        opacity: 0.72,
    },
    findifyBottomItemActive: {
        opacity: 1,
    },
    findifyBottomLabel: {
        textAlign: 'center',
        fontSize: 10,
        lineHeight: 13,
        fontWeight: '700',
    }
})
